import type { tickets } from '@repletoryreach/sdk-api';

/**
 * The automation builder's model (7 October 2026): what each condition field takes, which operators make sense
 * for it, and how a row on screen becomes the `{field, op, value}` the service stores. Pure, so it is tested.
 */

/** What kind of picker a condition's value needs. */
export type ValueKind =
  | 'channel' | 'keywords' | 'tag' | 'business_hours' | 'type' | 'priority' | 'category' | 'team' | 'number' | 'field';

export interface FieldDef {
  /** The stored field; `ticket.field` stands for every `ticket.field.<key>`. */
  field: string;
  kind: ValueKind;
  ops: readonly tickets.ConditionOp[];
  /** The triggers it is offered on. */
  triggers: readonly tickets.RuleTrigger[];
}

const ALL: readonly tickets.RuleTrigger[] = ['message_received', 'unanswered', 'ticket_created', 'ticket_updated', 'time_passed'];
const MESSAGE: readonly tickets.RuleTrigger[] = ['message_received', 'unanswered'];
const TICKET: readonly tickets.RuleTrigger[] = ['ticket_created', 'ticket_updated', 'time_passed'];

export const FIELD_DEFS: readonly FieldDef[] = [
  { field: 'channel', kind: 'channel', ops: ['eq', 'neq', 'in'], triggers: ALL },
  { field: 'text', kind: 'keywords', ops: ['contains_any', 'not_contains_any'], triggers: MESSAGE },
  { field: 'contact.tag', kind: 'tag', ops: ['eq', 'neq', 'in', 'is_set', 'not_set'], triggers: ALL },
  { field: 'business_hours', kind: 'business_hours', ops: ['eq'], triggers: ALL },
  { field: 'ticket.type_id', kind: 'type', ops: ['eq', 'neq', 'in'], triggers: TICKET },
  { field: 'ticket.priority_id', kind: 'priority', ops: ['eq', 'neq', 'in'], triggers: TICKET },
  { field: 'ticket.status_category', kind: 'category', ops: ['eq', 'neq', 'in'], triggers: TICKET },
  { field: 'ticket.team_id', kind: 'team', ops: ['eq', 'neq', 'in', 'is_set', 'not_set'], triggers: TICKET },
  { field: 'ticket.tag', kind: 'tag', ops: ['eq', 'neq', 'in', 'is_set', 'not_set'], triggers: TICKET },
  { field: 'ticket.field', kind: 'field', ops: ['eq', 'neq', 'contains_any', 'is_set', 'not_set', 'gte', 'lte'], triggers: TICKET },
  { field: 'minutes_in_status', kind: 'number', ops: ['gte', 'lte'], triggers: ['time_passed', 'ticket_updated'] }
];

/** The channels a message can arrive on. */
export const CHANNELS = ['whatsapp', 'messenger', 'instagram', 'web', 'email', 'sms', 'voice'] as const; // i18n-ignore: channel codes

const FIELD_PREFIX = 'ticket.field.';

/** The definition behind a stored field (`ticket.field.size` is `ticket.field`). */
export function fieldDef(field: string): FieldDef | undefined {
  if (field.startsWith(FIELD_PREFIX)) return FIELD_DEFS.find((d) => d.field === 'ticket.field');
  return FIELD_DEFS.find((d) => d.field === field);
}

export function fieldsForTrigger(trigger: tickets.RuleTrigger): FieldDef[] {
  return FIELD_DEFS.filter((d) => d.triggers.includes(trigger));
}

export function opTakesNoValue(op: tickets.ConditionOp): boolean {
  return op === 'is_set' || op === 'not_set';
}

export function opTakesList(op: tickets.ConditionOp): boolean {
  return op === 'in' || op === 'contains_any' || op === 'not_contains_any';
}

/** A condition row as the builder edits it. Lists are typed as comma-separated text or picked as an array. */
export interface ConditionDraft {
  field: string;
  op: tickets.ConditionOp;
  value: string | string[];
}

export function newCondition(trigger: tickets.RuleTrigger): ConditionDraft {
  const def = fieldsForTrigger(trigger)[0] ?? FIELD_DEFS[0]!;
  return { field: def.field === 'ticket.field' ? FIELD_PREFIX : def.field, op: def.ops[0]!, value: '' };
}

/** Comma- or newline-separated text to a clean list: trimmed, empty ones dropped, duplicates removed. */
export function splitList(value: string | string[]): string[] {
  const parts = Array.isArray(value) ? value : value.split(/[,\n]/);
  const out: string[] = [];
  for (const p of parts) {
    const v = String(p).trim();
    if (v && !out.some((o) => o.toLowerCase() === v.toLowerCase())) out.push(v);
  }
  return out;
}

/** The stored condition, or null when the row is not finished (no value where one is needed). */
export function serialiseCondition(d: ConditionDraft): tickets.Condition | null {
  const def = fieldDef(d.field);
  if (!def) return null;
  if (def.kind === 'field' && d.field.length <= FIELD_PREFIX.length) return null;
  if (opTakesNoValue(d.op)) return { field: d.field, op: d.op };
  if (opTakesList(d.op)) {
    const list = splitList(d.value);
    return list.length ? { field: d.field, op: d.op, value: list } : null;
  }
  const raw = Array.isArray(d.value) ? d.value[0] ?? '' : d.value;
  const text = String(raw).trim();
  if (!text) return null;
  if (def.kind === 'number' || d.op === 'gte' || d.op === 'lte') {
    const n = Number(text);
    return Number.isFinite(n) ? { field: d.field, op: d.op, value: n } : null;
  }
  return { field: d.field, op: d.op, value: text };
}

/** A stored condition back into a row. */
export function conditionDraft(c: tickets.Condition): ConditionDraft {
  const v = c.value;
  const value = Array.isArray(v) ? v.map(String) : v == null ? '' : String(v);
  return { field: c.field, op: c.op, value: opTakesList(c.op) && !Array.isArray(value) ? splitList(value) : value };
}

/**
 * The `unanswered` trigger's "after N minutes" is stored as a `minutes gte N` condition; the builder shows it as
 * its own input. This pulls it out of the stored list.
 */
export function splitMinutes(conditions: readonly tickets.Condition[]): { minutes: number | null; rest: tickets.Condition[] } {
  let minutes: number | null = null;
  const rest: tickets.Condition[] = [];
  for (const c of conditions) {
    if (c.field === 'minutes' && minutes === null && typeof c.value === 'number') minutes = c.value;
    else if (c.field === 'minutes' && minutes === null && typeof c.value === 'string' && Number.isFinite(Number(c.value))) minutes = Number(c.value);
    else rest.push(c);
  }
  return { minutes, rest };
}

/** An action as the builder edits it: any of the stored shapes. */
export type ActionDraft = tickets.RuleAction;

export function newAction(type: tickets.ActionType): ActionDraft {
  switch (type) {
    case 'create_ticket': return { type, subject: '{{contact.name}}: {{text}}' }; // i18n-ignore: template placeholders
    case 'assign': return { type, team_id: null, user_id: null, round_robin: false };
    case 'set_status': return { type, status_id: '' };
    case 'set_priority': return { type, priority_id: '' };
    case 'add_tag': return { type, tag: '' };
    case 'set_field': return { type, key: '', value: '' };
    case 'notify': return { type, to: 'assignee', user_id: null };
    case 'reply_client': return { type, text: '' };
    case 'add_task': return { type, kind: 'call', title: '', due_minutes: 60 };
    case 'close': return { type };
  }
}

/** The actions a trigger may use: a ticket can only be opened by a rule that starts from a message. */
export function actionsForTrigger(trigger: tickets.RuleTrigger): tickets.ActionType[] {
  const all: tickets.ActionType[] = ['create_ticket', 'assign', 'set_status', 'set_priority', 'add_tag', 'set_field', 'notify', 'reply_client', 'add_task', 'close'];
  return trigger === 'message_received' || trigger === 'unanswered' ? all : all.filter((a) => a !== 'create_ticket');
}

/** A catalogue key naming what is missing from an action, or null when it is complete. */
export function actionProblem(a: ActionDraft): string | null {
  switch (a.type) {
    case 'assign': return a.team_id || a.user_id ? null : 'tickets.rules.error.assign';
    case 'set_status': return a.status_id ? null : 'tickets.rules.error.status';
    case 'set_priority': return a.priority_id ? null : 'tickets.rules.error.priority';
    case 'add_tag': return a.tag.trim() ? null : 'tickets.rules.error.tag';
    case 'set_field': return a.key ? null : 'tickets.rules.error.field';
    case 'notify': return a.to !== 'user' || a.user_id ? null : 'tickets.rules.error.notifyUser';
    case 'reply_client': return a.text.trim() ? null : 'tickets.rules.error.text';
    case 'add_task': return a.title.trim() ? null : 'tickets.rules.error.taskTitle';
    default: return null;
  }
}

/** The action as stored: empty optional ids dropped, text trimmed. */
export function serialiseAction(a: ActionDraft): tickets.RuleAction {
  const clean = <T extends object>(o: T): T =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined)) as T;
  switch (a.type) {
    case 'create_ticket': return clean({ ...a, subject: a.subject?.trim() || null });
    case 'assign': return a.round_robin ? clean({ type: a.type, team_id: a.team_id, round_robin: true }) : clean({ ...a, round_robin: undefined });
    case 'add_tag': return { type: a.type, tag: a.tag.trim() };
    case 'reply_client': return { type: a.type, text: a.text.trim() };
    case 'add_task': return clean({ ...a, title: a.title.trim() });
    case 'notify': return a.to === 'user' ? clean(a) : { type: a.type, to: a.to };
    default: return a;
  }
}

export interface RuleDraft {
  name: string;
  trigger: tickets.RuleTrigger;
  /** For `unanswered`: how long the client has waited. */
  minutes: number | '';
  conditions: ConditionDraft[];
  actions: ActionDraft[];
  stop_after: boolean;
  active: boolean;
}

export function ruleDraft(r: tickets.Rule | null): RuleDraft {
  if (!r) return { name: '', trigger: 'message_received', minutes: 30, conditions: [], actions: [], stop_after: false, active: true };
  const { minutes, rest } = splitMinutes(r.conditions ?? []);
  return {
    name: r.name,
    trigger: r.trigger,
    minutes: minutes ?? 30,
    conditions: rest.map(conditionDraft),
    actions: (r.actions ?? []).map((a) => ({ ...a })),
    stop_after: r.stop_after,
    active: r.active
  };
}

/** A catalogue key naming the first problem with the draft, or null. */
export function ruleProblem(d: RuleDraft): string | null {
  if (!d.name.trim()) return 'tickets.rules.error.name';
  if (d.trigger === 'unanswered' && (d.minutes === '' || d.minutes < 1)) return 'tickets.rules.error.minutes';
  if (d.conditions.some((c) => serialiseCondition(c) === null)) return 'tickets.rules.error.condition';
  if (d.actions.length === 0) return 'tickets.rules.error.noActions';
  const allowed = actionsForTrigger(d.trigger);
  if (d.actions.some((a) => !allowed.includes(a.type))) return 'tickets.rules.error.createTicketTrigger';
  for (const a of d.actions) {
    const p = actionProblem(a);
    if (p) return p;
  }
  return null;
}

/** The body the service stores. The minutes condition goes first for `unanswered`, and only there. */
export function toRuleInput(d: RuleDraft, sortOrder: number): tickets.RuleInput {
  const conditions = d.conditions
    .filter((c) => fieldDef(c.field)?.triggers.includes(d.trigger))
    .map(serialiseCondition)
    .filter((c): c is tickets.Condition => c !== null);
  if (d.trigger === 'unanswered') conditions.unshift({ field: 'minutes', op: 'gte', value: d.minutes === '' ? 30 : d.minutes });
  return {
    name: d.name.trim(),
    trigger: d.trigger,
    conditions,
    actions: d.actions.map(serialiseAction),
    sort_order: sortOrder,
    stop_after: d.stop_after,
    active: d.active
  };
}

/** Move one item of a list to another position (the rules' drag order). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list];
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) return out;
  const [item] = out.splice(from, 1);
  out.splice(to, 0, item as T);
  return out;
}
